import { DestroyRef, inject, Injector } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { createSelector, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  combineLatest,
  filter,
  map,
  mergeMap,
  Observable,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { HotkeysService, onLanguageChange } from 'wflow-cdk';
import {
  WfNavigationFacadeService,
  WfNavigationParams,
  WfNavigationSource,
} from 'wflow-features/navigation';
import { ProductEventTrackingService } from 'wflow-features/product-analytics';
import { AuthService } from 'wflow-main-app/src/app/auth/auth.service';
import { HotkeysInfoComponent } from 'wflow-main-app/src/app/features/hotkeys-info';
import { APP_ROUTES } from 'wflow-main-app/src/app/routing.const';
import { WfDialogService } from 'wflow-ui';
import { ProfileService } from '../../../core/services/profile.service';
import { VersionService } from '../../../core/services/version.service';
import { selectCurrentOrganizationLanguage } from '../../../store/settings/settings.selectors';
import { UserStore } from '../../../store/user/user.model';
import {
  selectAccountIsSupport,
  selectUserAccount,
  selectUserLanguage,
} from '../../../store/user/user.selectors';
import { PageContainerService } from '../../components/page-container/page-container.service';
import { WfAuthService } from 'wflow-features/auth';
import { BinarbaseAvailabilityService } from '../binarbase/binarbase-availability.service';

const userLanguage = createSelector(
  selectUserLanguage,
  selectCurrentOrganizationLanguage,
  (userLang, orgLang) => (userLang === 'od' ? orgLang : userLang) ?? 'cs',
);

/** wflow's knowledge base — the Intercom help centre and every article under it. */
const KNOWLEDGE_BASE_URL = 'https://support.wflow.com';

const translateChanged = () => {
  const tr = inject(TranslateService);
  const injector = inject(Injector);
  return onLanguageChange(200, injector).pipe(
    map(() => tr.currentLang),
    shareReplay(0),
  );
};

export const GLOBAL_NAVIGATION: () => WfNavigationSource = () => {
  const tr = inject(TranslateService);
  const store = inject<Store<UserStore>>(Store);
  const binarbaseAvailabilitySvc = inject(BinarbaseAvailabilityService);
  const authSvc = inject(WfAuthService);
  const destroyRef = inject(DestroyRef);
  const productTrackingSvc = inject(ProductEventTrackingService);

  /**
   * Opens a Help-menu resource and reports which one. `resource` is a fixed slug,
   * never the URL or the translated label, so the payload stays bounded.
   *
   * - Area S P2 [Frequency] — `core_help_link_clicked` on every item, the
   *   follow-through to the `nav_help_opened` menu open.
   * - Area S P2 [Adoption] — `core_kb_article_opened` additionally for the Intercom
   *   help centre and its articles, which are wflow's knowledge base. Webinars, the
   *   product portal, the status page and the legal pages are not KB content.
   */
  const helpLinkOpener = (resource: string, link: string) => () => {
    productTrackingSvc.track('core_help_link_clicked', resource);

    if (link.startsWith(KNOWLEDGE_BASE_URL)) {
      productTrackingSvc.track('core_kb_article_opened', resource);
    }

    return window.open(link, '_blank');
  };

  /**
   * Area Q P2 [Adoption] — which report the BI sidebar group sent the user to. The
   * page reports the open itself as `bi_dashboard_opened`, but it cannot say which
   * entry was picked: it only sees the resolved (signed) dashboard URL.
   */
  const biNavEntry = (report: string) => () =>
    productTrackingSvc.track('bi_nav_entry_clicked', report);

  const isAuthReady = authSvc.loginResponse$.pipe(
    filter((authState) => !!authState?.accessToken),
    take(1),
    takeUntilDestroyed(destroyRef),
  );

  return isAuthReady.pipe(
    switchMap(() =>
      combineLatest([
        binarbaseAvailabilitySvc.isBinarbaseBetaEnabled(),
        store.select(selectAccountIsSupport),
        store.select(userLanguage),
      ]),
    ),
    map(([binarbaseEnabled, isSupport, userLang]) => {
      const showBinarbase = binarbaseEnabled && isSupport === false;
      const isCzOrSkLang = userLang === 'cs' || userLang === 'sk';
      return {
        primary: [
          showBinarbase && {
            path: '/',
            label: tr.instant('Dashboard'),
            icon: 'home-05',
            hideInContext: true,
          },
          showBinarbase && {
            id: `${APP_ROUTES.BINARBASE}-group`,
            path: 'wflow-bi',
            label: tr.instant('REPORTING.Reporting'),
            icon: 'bar-chart-10',
            hideInContext: true,
            isGroup: true,
            children: [
              {
                icon: 'file-check-02',
                label: tr.instant('REPORTING.ReportingProcessingEfficiency'),
                path: ['wflow-bi', 'processing-efficiency'],
                onClick: biNavEntry('processing_efficiency'),
              },

              {
                icon: 'file-check-02',
                label: tr.instant('REPORTING.ReportingApprovalEfficiency'),
                path: ['wflow-bi', 'approval-efficiency'],
                onClick: biNavEntry('approval_efficiency'),
              },

              {
                icon: 'file-check-02',
                label: tr.instant('REPORTING.ReportingDocuments'),
                path: ['wflow-bi', 'documents'],
                onClick: biNavEntry('documents'),
              },

              {
                icon: 'file-income-02',
                label: tr.instant('REPORTING.ReportingOverviewInvoices'),
                path: ['wflow-bi', 'incoming-invoices'],
                onClick: biNavEntry('incoming_invoices'),
              },

              {
                icon: 'file-outcome-02',
                label: tr.instant(
                  'REPORTING.ReportingOverviewOutgoingInvoices',
                ),
                path: ['wflow-bi', 'outgoing-invoices'],
                onClick: biNavEntry('outgoing_invoices'),
              },
            ],
          },
        ],
        secondary: [
          {
            id: 'help',
            label: tr.instant('Help'),
            icon: 'help-circle',
            modal: true,
            // Area A [Frustration] — the sidebar-footer help entry point. The
            // dropdown open is the access moment; the item click only says which
            // resource was picked. A `modal` node cannot use `onClick` (it would
            // disable the dropdown), hence the dedicated menu-open hook.
            onMenuOpen: () => productTrackingSvc.track('nav_help_opened'),
            children: [
              {
                icon: 'phone-01',
                label: tr.instant('MobileApp'),
                onClick: helpLinkOpener(
                  'mobile_app_article',
                  isCzOrSkLang
                    ? `${KNOWLEDGE_BASE_URL}/cs/articles/3580473-mobilni-aplikace`
                    : `${KNOWLEDGE_BASE_URL}/en/articles/3580473-mobile-application`,
                ),
              },
              {
                icon: 'book-open-01',
                label: tr.instant('HelpCenter'),
                onClick: helpLinkOpener('help_center', KNOWLEDGE_BASE_URL),
              },

              {
                icon: 'play-circle',
                label: tr.instant('Webinars'),
                onClick: helpLinkOpener(
                  'webinars',
                  'https://wflow.com/novinky/webinare/',
                ),
              },
              {
                icon: 'announcement-02',
                label: tr.instant('VoteOnFeatures'),
                onClick: helpLinkOpener(
                  'product_portal',
                  'https://portal.productboard.com/wflow/2-wflow-produktovy-portal',
                ),
              },
              {
                icon: 'server-02',
                label: tr.instant('Statuspage'),
                onClick: helpLinkOpener(
                  'status_page',
                  'https://wflow.statuspage.io/',
                ),
              },
              { divider: true, label: '' },
              {
                label: tr.instant('GeneralTermsAndConditions'),
                onClick: helpLinkOpener(
                  'terms_and_conditions',
                  isCzOrSkLang
                    ? 'https://www.wflow.com/vseobecne-obchodni-podminky'
                    : 'https://www.wflow.com/en/terms-and-conditions',
                ),
              },
              {
                label: tr.instant('PrivacyPolicy'),
                onClick: helpLinkOpener(
                  'privacy_policy',
                  isCzOrSkLang
                    ? 'https://www.wflow.com/gdpr'
                    : 'https://www.wflow.com/en/privacy-policy',
                ),
              },
            ],
          },
        ],
      };
    }),
  );
};

export const GLOBAL_NAVIGATION_PARAMS: () => WfNavigationParams = () => {
  const tr = inject(TranslateService);
  const store = inject<Store<UserStore>>(Store);
  const router = inject(Router);
  const authSvc = inject(AuthService);
  const versionSvc = inject(VersionService);
  const profileSvc = inject(ProfileService);
  const navService = inject(WfNavigationFacadeService);
  const dialogSvc = inject(WfDialogService);
  const hotkeysSvc = inject(HotkeysService);
  const productTrackingSvc = inject(ProductEventTrackingService);

  const logo$ = toObservable(inject(PageContainerService).customerLogo).pipe(
    map((logo) => logo ?? 'assets/images/wflow-logo-placeholder.png'),
  );
  const navigationMode$ = toObservable(navService.barAnimation.navigationMode);
  const data$ = translateChanged().pipe(
    mergeMap(() =>
      combineLatest([store.select(selectUserAccount), navigationMode$]),
    ),
  );

  const openHotkeysSettings = () => {
    dialogSvc.open(HotkeysInfoComponent, {
      title: tr.instant('HOTKEYS.Title'),
      width: '800px',
      data: {
        registeredHotkeys: hotkeysSvc.getAllRegistered(),
      },
    });
  };

  return {
    search: false,
    version: versionSvc.currentVersion,

    getLogo: () => logo$,

    getUser: () =>
      data$.pipe(
        filter(([user]) => !!user),
        map(([user, navigationMode]) => ({
          label: user?.fullName,
          email: user?.login,
          children: [
            {
              icon: 'user-circle',
              label: tr.instant('ManageMyAccount'),
              onClick: () => {
                // Area A [Frequency] — the profile shortcut in the avatar menu.
                productTrackingSvc.track('nav_profile_shortcut_open');

                profileSvc.openProfileDetailsDialog();
              },
            },
            {
              icon: 'layout-alt-02',
              label: tr.instant('NAVIGATION_BAR.ModeSettings'),
              children: [
                {
                  active: navigationMode === 'auto',
                  icon: 'layout-left',
                  label: tr.instant('NAVIGATION_BAR.AutoMode'),
                  onClick: () => {
                    navService.barAnimation.setNavigationMode('auto');
                  },
                },
                {
                  active: navigationMode === 'minimize',
                  icon: 'flex-align-left',
                  label: tr.instant('NAVIGATION_BAR.StaticMode'),
                  onClick: () => {
                    navService.barAnimation.setNavigationMode('minimize');
                  },
                },
              ],
            },
            {
              icon: 'command',
              label: tr.instant('KeyboardShortcuts'),
              onClick: () => openHotkeysSettings(),
            },
            ...(user.isCustomerAdmin
              ? [
                  {
                    label: tr.instant('AdminAppLink'),
                    icon: 'building-07',
                    path: '/customer',
                  },
                ]
              : []),
            { divider: true, label: '' },
            {
              icon: 'log-out-01',
              label: tr.instant('SignOut'),
              onClick: () => {
                // Area I [Frequency] — the user deliberately ended the session
                // from the account menu. Fired at the click, before the logout
                // request, and distinct from the automatic idle logout.
                productTrackingSvc.track('onb_manual_logout', 1);
                // Area A [Retention] — the same click counted in the shell
                // (session-end baseline), per the catalog's two entries.
                productTrackingSvc.track('nav_logout_click', 1);

                (
                  authSvc.logout({
                    redirect_uri: router.url,
                  }) as Observable<void>
                )
                  .pipe(take(1))
                  .subscribe(() => {
                    localStorage.removeItem('0-wflow_main_app');
                    router.navigate([`/${APP_ROUTES.LOGOUT}`]);
                  });
              },
            },
          ],
        })),
      ),
  };
};
